/**
 * Business Templates Page
 * Prompt Templates, Artifact Types, and Output Schemas management
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import {
  ArtifactTypesPanel,
  PromptTemplatesPanel,
  OutputSchemasPanel,
} from '@/features/system-settings';
import { PromptTemplatesContent } from '@/features/prompt-templates';
import { Package, FileText, FileJson, ScrollText } from 'lucide-react';
import { useI18n } from '@/i18n';

const VALID_TABS = ['prompt-templates', 'artifact-types', 'output-prompt-templates', 'output-schemas'];

export function BusinessTemplatesPage() {
  const { messages } = useI18n();
  const t = messages.businessTemplates;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'prompt-templates';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync tab changes to URL
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Update tab when URL changes (e.g., browser back/forward)
  useEffect(() => {
    if (tabFromUrl && VALID_TABS.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t.pageTitle}</h1>
        <p className="text-gray-600 mt-1">
          {t.pageDescription}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-6">
          <TabsTrigger value="prompt-templates" className="flex items-center gap-2">
            <ScrollText className="w-4 h-4" />
            {t.tabs.promptTemplates}
          </TabsTrigger>
          <TabsTrigger value="artifact-types" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            {t.tabs.artifactTypes}
          </TabsTrigger>
          <TabsTrigger value="output-prompt-templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {t.tabs.outputPromptTemplates}
          </TabsTrigger>
          <TabsTrigger value="output-schemas" className="flex items-center gap-2">
            <FileJson className="w-4 h-4" />
            {t.tabs.outputSchemas}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="prompt-templates">
          <PromptTemplatesContent />
        </TabsContent>

        <TabsContent value="artifact-types">
          <ArtifactTypesPanel />
        </TabsContent>

        <TabsContent value="output-prompt-templates">
          <PromptTemplatesPanel />
        </TabsContent>

        <TabsContent value="output-schemas">
          <OutputSchemasPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
